<template>
  <main class="duel-student container py-4">
    <div class="d-flex justify-content-end mb-3">
      <button class="btn btn-sm btn-outline-secondary" type="button" @click="switchAccessCode">切換通行密鑰</button>
    </div>
    <b-alert v-if="error" variant="danger" show>{{ error }}</b-alert>
    <div v-else-if="loading" class="text-center text-muted py-5">載入題目中…</div>
    <template v-else-if="config">
      <section class="card shadow-sm mb-4">
        <div class="card-body">
          <h1 class="h3">{{ config.name }}</h1>
          <p class="text-muted mb-3">NPC A：{{ npcAName }} / NPC B：{{ npcBName }}</p>
          <dl class="row mb-0">
            <dt class="col-sm-3">對戰目標</dt><dd class="col-sm-9">{{ config.goal }}</dd>
            <dt class="col-sm-3">先攻方</dt><dd class="col-sm-9">NPC {{ config.starter }}</dd>
            <dt class="col-sm-3">回合數</dt><dd class="col-sm-9">{{ config.rounds }}</dd>
            <dt class="col-sm-3">判定規則</dt><dd class="col-sm-9">{{ ruleText }}</dd>
          </dl>
        </div>
      </section>

      <section v-if="!playerName" class="card shadow-sm mb-4">
        <div class="card-body">
          <h2 class="h5">輸入你的名字</h2>
          <p class="text-muted">你的測試紀錄會以這個名字保存，請使用老師指定的名字（例如學號或姓名）。</p>
          <form class="d-flex" @submit.prevent="confirmPlayerName">
            <input v-model="nameInput" class="form-control" maxlength="64" placeholder="名字" aria-label="名字" />
            <button class="btn btn-primary ml-2 text-nowrap" type="submit" :disabled="!nameInput.trim()">開始</button>
          </form>
        </div>
      </section>

      <template v-else>
      <p class="text-muted">
        目前名字：<strong>{{ playerName }}</strong>
        <button class="btn btn-link btn-sm p-0 ml-2 align-baseline" type="button" @click="changePlayerName">更換名字</button>
      </p>

      <section class="card shadow-sm mb-4">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h2 class="h5 mb-0">輸入對戰條件</h2>
            <small class="text-muted">{{ conditions.length }} / 10 條；每條最多 500 字</small>
          </div>
          <p class="text-muted">請列出希望兩位 NPC 在對話中遵守、討論或達成的條件。</p>
          <div v-for="(condition, index) in conditions" :key="index" class="form-group d-flex">
            <textarea v-model="conditions[index]" class="form-control" rows="2" maxlength="500" :placeholder="`條件 ${index + 1}`" @input="persistConditions"></textarea>
            <button v-if="conditions.length > 1" class="btn btn-outline-danger ml-2 align-self-start" type="button" :aria-label="`刪除條件 ${index + 1}`" @click="removeCondition(index)">刪除</button>
          </div>
          <div class="d-flex flex-wrap align-items-center">
            <button class="btn btn-outline-secondary mr-2" type="button" :disabled="conditions.length >= 10" @click="addCondition">新增條件</button>
            <button class="btn btn-primary" type="button" :disabled="!hasConditions || submitting" @click="prepareRun">{{ submitting ? '建立中…' : '測試' }}</button>
          </div>
          <p v-if="notice" class="text-info mb-0 mt-3">{{ notice }}</p>
        </div>
      </section>

      <section class="card shadow-sm">
        <div class="card-body">
          <h2 class="h5">對戰結果</h2>
          <div v-if="result" class="border rounded p-3">
            <p class="mb-1"><strong>狀態：</strong>{{ resultStatus }}</p>
            <p v-if="result.runId" class="mb-1"><strong>Run ID：</strong>{{ result.runId }}</p>
            <p v-if="resultConditions.length" class="mb-1"><strong>條件：</strong>{{ resultConditions.join('、') }}</p>
            <p class="mb-1"><strong>判定：</strong>{{ resultLabel }}</p>
            <p v-if="result.judgeReason" class="mb-0"><strong>說明：</strong>{{ result.judgeReason }}</p>
            <p v-if="result.error" class="text-danger mb-0"><strong>錯誤：</strong>{{ result.error }}</p>
            <DuelTranscript v-if="result.records" class="mt-3" :records="result.records" :npc-a="config.npcA" />
          </div>
          <p v-else class="text-muted mb-0">尚未執行對戰。送出條件後，對話進度與判定結果會顯示在這裡。</p>
        </div>
      </section>

      <section class="card shadow-sm mt-4">
        <div class="card-body">
          <h2 class="h5">過去測試紀錄</h2>
          <div class="table-responsive">
            <table class="table table-sm table-hover mb-0">
              <thead><tr><th>時間</th><th>條件快照</th><th>狀態</th><th>結果</th><th></th></tr></thead>
              <tbody>
                <tr v-for="run in history" :key="run.runId">
                  <td>{{ run.createdAt }}</td><td>{{ conditionSummary(run) }}</td><td>{{ run.status }}</td><td>{{ historyResult(run) }}</td>
                  <td class="text-right"><button class="btn btn-sm btn-outline-primary" @click="showHistory(run)">查看</button></td>
                </tr>
                <tr v-if="history.length === 0"><td colspan="5" class="text-center text-muted">尚無測試紀錄。</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
      </template>
    </template>
  </main>
</template>

<script>
import authStorage from "../../authStorage";
import axios from "axios";
import DuelTranscript from "./DuelTranscript.vue";
import { conditionContent, resultText } from "../../utils/duelData.mjs";
import { pollUntil } from "../../utils/jobPolling";

const API_BASE = process.env.VUE_APP_API_BASE || "https://api.ntustmeg.com";

export default {
  name: "DuelStudent",
  components: { DuelTranscript },
  data() { return { config: null, playerName: "", nameInput: "", conditions: [""], history: [], loading: true, submitting: false, error: "", notice: "", result: null, pollHandle: null }; },
  computed: {
    duelId() { return String(this.$route.query.duel || "").trim(); },
    storageKey() { return `duel-conditions:${this.duelId}`; },
    nameStorageKey() { return `duel-player-name:${this.duelId}`; },
    hasConditions() { return this.conditions.some((condition) => String(condition).trim()); },
    npcAName() { return this.config.npcAShowName || this.config.npcA; },
    npcBName() { return this.config.npcBShowName || this.config.npcB; },
    ruleText() {
      if (this.config.goalType === "score") return `分數介於 ${this.config.scoreMin} 至 ${this.config.scoreMax}，達 ${this.config.scoreThreshold} 分以上即通過。`;
      return "AI 會判定是否通過目標。";
    },
    resultLabel() {
      return resultText(this.result);
    },
    resultStatus() {
      return this.result.status;
    },
    resultConditions() {
      if (!this.result) return [];
      if (Array.isArray(this.result.conditions)) return this.result.conditions;
      return conditionContent(this.result).conditions;
    },
  },
  async created() {
    if (!this.duelId || !/^\d+$/.test(this.duelId)) { this.error = "缺少有效的對戰題目。"; this.loading = false; return; }
    this.restoreConditions();
    try {
      this.config = (await axios.get(`${API_BASE}/api/duel/${encodeURIComponent(this.duelId)}`)).data;
      let savedName = "";
      try { savedName = String(localStorage.getItem(this.nameStorageKey) || "").trim(); } catch (_) { /* Ignore unavailable browser storage. */ }
      if (savedName) {
        this.playerName = savedName;
        await this.enterAsPlayer();
      }
    }
    catch (err) { this.error = err?.response?.data?.message || "無法讀取對戰題目。"; }
    finally { this.loading = false; }
  },
  beforeDestroy() { if (this.pollHandle) this.pollHandle.cancel(); },
  methods: {
    async confirmPlayerName() {
      const name = this.nameInput.trim().slice(0, 64);
      if (!name) return;
      this.playerName = name;
      try { localStorage.setItem(this.nameStorageKey, name); } catch (_) { /* Ignore unavailable browser storage. */ }
      try { await this.enterAsPlayer(); }
      catch (err) { this.notice = err?.response?.data?.message || "無法讀取測試紀錄。"; }
    },
    changePlayerName() {
      if (this.pollHandle) { this.pollHandle.cancel(); this.pollHandle = null; }
      this.nameInput = this.playerName;
      this.playerName = "";
      this.history = [];
      this.result = null;
      this.notice = "";
      try { localStorage.removeItem(this.nameStorageKey); } catch (_) { /* Ignore unavailable browser storage. */ }
    },
    // 讀取此名字的紀錄，若有尚未完成的測試則繼續輪詢
    async enterAsPlayer() {
      await this.loadHistory();
      const active = this.history.find((run) => run.status === "running");
      if (active) {
        this.result = { ...active, records: [] };
        this.notice = "偵測到尚未完成的測試，正在繼續讀取進度。";
        this.startPolling(active.runId);
      }
    },
    switchAccessCode() {
      ["session_token", "game_id", "session_allowed_ch", "admin_role"].forEach((key) => authStorage.removeItem(key));
      this.$router.replace({ path: "/login", query: { duel: this.duelId } });
    },
    restoreConditions() {
      try {
        const saved = JSON.parse(localStorage.getItem(this.storageKey));
        if (Array.isArray(saved) && saved.length > 0) this.conditions = saved.slice(0, 10).map((item) => String(item).slice(0, 500));
      } catch (_) { /* Ignore malformed browser storage. */ }
    },
    persistConditions() { localStorage.setItem(this.storageKey, JSON.stringify(this.conditions.map((item) => String(item).slice(0, 500)))); },
    addCondition() { if (this.conditions.length < 10) { this.conditions.push(""); this.persistConditions(); } },
    removeCondition(index) { if (this.conditions.length > 1) { this.conditions.splice(index, 1); this.persistConditions(); } },
    async prepareRun() {
      this.conditions = this.conditions.map((item) => String(item).trim()).filter(Boolean).slice(0, 10);
      if (this.conditions.length === 0) { this.conditions = [""]; return; }
      this.persistConditions();
      this.submitting = true;
      this.notice = "";
      try {
        const response = await axios.post(`${API_BASE}/api/duel/${encodeURIComponent(this.duelId)}/runs`, { conditions: this.conditions, playerName: this.playerName });
        this.result = response.data;
        this.notice = "測試已建立，正在產生對話。";
        this.startPolling(response.data.runId);
      } catch (err) {
        const data = err?.response?.data || {};
        if (err?.response?.status === 409 && data.runId) {
          this.result = { runId: data.runId, status: "running", passed: null, records: [] };
          this.startPolling(data.runId);
        }
        this.notice = data.message || "無法建立執行資料。";
      } finally {
        this.submitting = false;
      }
    },
    async loadHistory() {
      const response = await axios.get(`${API_BASE}/api/duel/${encodeURIComponent(this.duelId)}/runs`, { params: { playerName: this.playerName } });
      this.history = Array.isArray(response.data.runs) ? response.data.runs : [];
    },
    startPolling(runId) {
      if (this.pollHandle) this.pollHandle.cancel();
      this.pollHandle = pollUntil(
        () => axios.get(`${API_BASE}/api/duel/runs/${encodeURIComponent(runId)}`).then((response) => response.data),
        (data) => data.status === "done" || data.status === "failed",
        { onUpdate: (data) => { this.result = data; } }
      );
      this.pollHandle.promise
        .then((data) => {
          if (!data) return null; // 被 cancel（例如更換名字）時不重新讀取紀錄
          this.notice = data.status === "done" ? "測試完成。" : "測試失敗，可修改條件後重新測試。";
          return this.loadHistory();
        })
        .catch((err) => { this.notice = err?.response?.data?.message || "讀取測試進度失敗。"; });
    },
    historyResult(run) { return resultText(run); },
    conditionSummary(run) {
      const conditions = conditionContent(run).conditions;
      return conditions.length ? conditions.join("、") : "-";
    },
    async showHistory(run) {
      try { this.result = (await axios.get(`${API_BASE}/api/duel/runs/${encodeURIComponent(run.runId)}`)).data; }
      catch (err) { this.notice = err?.response?.data?.message || "無法讀取測試紀錄。"; }
    },
  },
};
</script>

<style scoped>
.duel-student { max-width: 860px; }
textarea { resize: vertical; }
</style>
